/**
 * ParticlesStage — the particles-side scene graph of the home WebGL layer.
 *
 * Builds the home particle field's sub-container tree INSIDE the field's
 * particle container (which itself lives in the home container's webglMain
 * slot — see HomeStageSkeleton): icon, spacesOutter > spaces (+ spaces
 * mask), systems, screensOutter > screens (+ screens mask), the screens
 * mask image / darken / overlay layers (wired into HomeScreens), and the
 * transitions container — all mounted onto the field's particle container
 * in a specific child order. The cross-file refs the field used to
 * register on globals.* are typed members here instead — readers reach the
 * stage through HomeField.stage (ex globals.iconContainer / spacesOutter /
 * systemsContainer / spacesMask / ttt).
 *
 * IMPORTANT: creation and mount order here is load-bearing for the home
 * visuals (child order = draw order, and masks are wired to siblings
 * created here). This code is a verbatim extraction of the home-only block
 * that lived inline in particles/ParticleField.ts's constructor, and it
 * still runs at the exact same point in that constructor — ParticleField
 * invokes it via config.subContainersFactory (see HomeField).
 */
import HomeScreens from 'app/views/home/webgl/particles/scenes/HomeScreens';

export class ParticlesStage {
	private _subContainers: Record<string, any>;

	//Mask graphics for the spaces zone (SpacesDepartment redraws it on layout):
	public readonly spacesMask: PIXI.Graphics;
	//'Screens Mask Image' sprite (ScreensDepartment keeps it glued to
	//globals.framesprite each layout pass):
	public readonly ttt: PIXI.Sprite;

	constructor(particleContainer: PIXI.Container, textures: Record<string, PIXI.Texture>) {
		this._subContainers = {};

		this._subContainers.icon = new (PIXI.Container as any)({
			isRenderGroup: true
		});
		this._subContainers.icon.name = 'HOME CONTAINER';
		this._subContainers.icon.interactive = false;
		this._subContainers.icon.interactiveChildren = false;
		this._subContainers.icon.sortableChildren = false;

		this._subContainers.spacesOutter = new (PIXI.Container as any)({
			isRenderGroup: true
		});
		this._subContainers.spacesOutter.name = 'SPACES OUTTER';

		this._subContainers.spaces = new (PIXI.Container as any)({
		});
		this._subContainers.spaces.name = 'SPACES CONTAINER';
		this._subContainers.spaces.interactive = false;
		this._subContainers.spaces.interactiveChildren = false;
		this._subContainers.spaces.cullable = true;
		this._subContainers.spaces.sortableChildren = false;

		this._subContainers.spacesOutter.addChild(this._subContainers.spaces);
		this._subContainers.spacesOutter.interactive = false;
		this._subContainers.spacesOutter.interactiveChildren = false;
		this._subContainers.spacesOutter.sortableChildren = false;

		this._subContainers.systems = new (PIXI.Container as any)({
			isRenderGroup: true
		});
		this._subContainers.systems.name = 'SYSTEMS CONTAINER';
		this._subContainers.systems.interactive = false;
		this._subContainers.systems.interactiveChildren = false;
		this._subContainers.systems.cullable = true;
		this._subContainers.systems.sortableChildren = false;

		this._subContainers.screensOutter = new (PIXI.Container as any)({
			isRenderGroup: true
		});

		this._subContainers.screensOutter.name = 'SCREENS OUTTER';

		this._subContainers.screens = new (PIXI.Container as any)({});
		this._subContainers.screens.name = 'SCREENS CONTAINER';
		this._subContainers.screens.interactive = false;
		this._subContainers.screens.interactiveChildren = false;
		this._subContainers.screens.sortableChildren = false;

		HomeScreens.maskGraphic = new PIXI.Graphics();
		this._subContainers.screens.mask = HomeScreens.maskGraphic;

		HomeScreens.maskGraphic.name = 'SCREENS MASK';
		HomeScreens.maskGraphic.interactive = false;

		this._subContainers.screensOutter.addChild(this._subContainers.screens);
		this._subContainers.screensOutter.addChild(HomeScreens.maskGraphic);

		this.ttt = new PIXI.Sprite();
		this.ttt.interactive = false;
		this.ttt.name = 'Screens Mask Image';
		this.ttt.texture = textures['screen-frames-mask.png'];
		(this.ttt as any).anchor = { x: 0.5, y: 0.5 }; //Plain-object anchor: fine on the v8 runtime, not in the v5 types
		this.ttt.alpha = 0;



		this.spacesMask = new PIXI.Graphics();
		this.spacesMask.name = 'SPACES MASK';
		this._subContainers.spaces.mask = this.spacesMask;
		this.spacesMask.interactive = false;
		this._subContainers.spacesOutter.addChild(this.spacesMask);

		HomeScreens.darkenSprite = new PIXI.Sprite();

		HomeScreens.overlayContainer = new PIXI.Container();
		HomeScreens.overlayContainer.name = 'SCREEN OVERLAY / NOISE CONTAINER';
		HomeScreens.overlaySpriteTexture = textures['screensTVTopLeft-highlight.png'];
		HomeScreens.overlayContainer.mask = HomeScreens.maskGraphic;

		this._subContainers.transitions = new PIXI.Container();
		this._subContainers.transitions.name = 'TRANSITION CONTAINER';
		this._subContainers.transitions.visible = false;
		this._subContainers.transitions.interactive = false;
		this._subContainers.transitions.interactiveChildren = false;


		particleContainer.interactive = false;
		particleContainer.interactiveChildren = false;
		particleContainer.sortableChildren = false;

		particleContainer.name = 'PARTICLES CONTAINER';
		particleContainer.addChild(
			this._subContainers.icon,
			this._subContainers.spacesOutter,
			this._subContainers.screensOutter,
			this.ttt,
			HomeScreens.darkenSprite,
			HomeScreens.overlayContainer,
			this._subContainers.systems
		);
	}

	//GETTER / SETTER
	//-------------------------------------------------------------------------------------
	public get subContainers(): Record<string, PIXI.Container> {
		return this._subContainers;
	}

	//Icon-zone sub-container (ParticlesWebGL fades it over drag progress):
	public get iconContainer(): PIXI.Container {
		return this._subContainers.icon;
	}

	//Systems-zone sub-container (ParticlesWebGL fades it over drag progress):
	public get systemsContainer(): PIXI.Container {
		return this._subContainers.systems;
	}

	//Outer spaces-zone container (SpacesDepartment attaches the reflection
	//filter to it):
	public get spacesOutter(): PIXI.Container {
		return this._subContainers.spacesOutter;
	}

	//PUBLIC METHODS
	//-------------------------------------------------------------------------------------
	public getSubContainer(type: string) {
		return this._subContainers[type];
	}
}
